// src/utils/orderStages.js — v3
// CHANGED FROM v2 (19 Jul 2026): removed the DUPLICATE return-window
// countdown that was embedded directly into this file's status label
// ("Initiate Return (23h 45m left)") — OrderHistory.jsx v27 already
// renders its own separate, live-ticking, rounding-bug-fixed countdown
// text right below the Initiate Return button. Two countdowns showing
// the same thing was the duplication Amit flagged. This file's version
// is now just a static "Initiate Return" label; OrderHistory.jsx's
// live countdown (nowTick-based, updates every 60s) is the single
// source of truth for the actual remaining time shown to customers.
// The returnCountdown variable below is still used internally — only
// to decide WHETHER the window is open (the boolean gate that shows/
// hides the button) — it's just no longer stringified into the label.
// CHANGED FROM v1: the "Initiate Return" live-countdown state now uses
// red (not blue) per Amit's feedback — it's a red action deserving
// urgency, matching the red used on the actual Initiate Return / Submit
// Return Request buttons elsewhere. Everything else unchanged.
// src/utils/orderStages.js — v4
// CHANGED FROM v3 (22 Jul 2026): "Cashback Credited" label was
// ambiguous — a customer sees the exact same text whether cashback
// came from a normal delivery OR from a return settlement (see
// 'refund_choice_made' below, which already says "nb wallet credited"
// distinctly for returns). Changed to "Delivered — Cashback Credited"
// so it's unambiguous this is the normal delivery-completion state,
// not return-related. Shared file — this label change applies to both
// customer Order History and Admin Panel (both read this same
// function), which is correct here since it's a genuine wording fix,
// not an admin-only concern.
export const RETURN_WINDOW_HOURS = 24;
export const EARN_DELAY_HOURS = 24;
export const STAGE_COLORS = {
  blue: '#2F6FED',
  green: '#1B7A3D',
  red: '#D64545',
  grey: '#B8B4AC',
  navy: '#1B2A4A',
  gold: '#8A5A00',
};
function hoursSince(dateStr) {
  if (!dateStr) return Infinity;
  return (new Date() - new Date(dateStr)) / (1000 * 60 * 60);
}
function hoursRemaining(dateStr, windowHours) {
  const elapsed = hoursSince(dateStr);
  return Math.max(0, windowHours - elapsed);
}
export function formatCountdown(dateStr, windowHours) {
  const remaining = hoursRemaining(dateStr, windowHours);
  if (remaining <= 0) return null;
  const h = Math.floor(remaining);
  const m = Math.floor((remaining - h) * 60);
  return `${h}h ${m}m left`;
}
export function getItemStageDisplay(item) {
  if (!item.dispatchedDate) {
    return { key: 'preparing', label: 'Preparing your order', color: STAGE_COLORS.gold, actionable: false };
  }
  if (!item.parcelReceivedTime) {
    return { key: 'in_transit', label: 'In transit', color: STAGE_COLORS.blue, actionable: false, showParcelReceivedButton: true };
  }
  const stage = item.returnStage || '';
  if (!stage) {
    const returnCountdown = formatCountdown(item.parcelReceivedTime, RETURN_WINDOW_HOURS);
    const cashbackCountdown = formatCountdown(item.parcelReceivedTime, EARN_DELAY_HOURS);
    if (item.cashbackStatus === 'Credited') {
      return { key: 'cashback_credited', label: 'Delivered — Cashback Credited', color: STAGE_COLORS.green, actionable: false };
    }
    if (returnCountdown) {
      return {
        key: 'return_window_open', label: 'Initiate Return', color: STAGE_COLORS.red,
        actionable: true, showInitiateReturnButton: true,
        secondaryLabel: cashbackCountdown ? `Cashback Credit in ${cashbackCountdown}` : 'Cashback processing…',
      };
    }
    return {
      key: 'return_window_closed', label: 'Return window closed', color: STAGE_COLORS.grey, actionable: false,
      secondaryLabel: cashbackCountdown ? `Cashback Credit in ${cashbackCountdown}` : 'Cashback processing…',
    };
  }
  if (stage === 'Requested') {
    return { key: 'return_requested', label: 'Return requested — awaiting review', color: STAGE_COLORS.gold, actionable: false };
  }
  if (stage === 'Rejected') {
    return { key: 'return_rejected', label: 'Return not approved', color: STAGE_COLORS.red, actionable: false, showContactUs: true };
  }
  if (stage === 'Approved') {
    return { key: 'return_approved', label: 'Return approved — pickup being arranged', color: STAGE_COLORS.green, actionable: false };
  }
  if (stage === 'Scheduled') {
    return { key: 'return_scheduled', label: 'Return pickup scheduled', color: STAGE_COLORS.green, actionable: false };
  }
  if (stage === 'Picked') {
    return { key: 'return_picked', label: 'Return picked up', color: STAGE_COLORS.green, actionable: false };
  }
  if (stage === 'Received') {
    if (item.refundOrWalletChoice) {
      return {
        key: 'refund_choice_made',
        label: item.refundOrWalletChoice === 'Wallet' ? 'nb wallet credited' : 'Refund processed',
        color: STAGE_COLORS.green, actionable: false,
      };
    }
    return { key: 'awaiting_choice', label: 'Choose refund or wallet credit', color: STAGE_COLORS.blue, actionable: true, showChoiceButtons: true };
  }
  return { key: 'unknown', label: stage, color: STAGE_COLORS.grey, actionable: false };
}
export function getAdminNextAction(item) {
  const stage = item.returnStage || '';
  if (stage === 'Requested') {
    return { key: 'approve_or_reject', approveLabel: 'Approve Return', rejectLabel: 'Reject Return' };
  }
  if (stage === 'Approved') {
    return { key: 'mark_scheduled', label: 'Mark Return Scheduled', color: STAGE_COLORS.green };
  }
  if (stage === 'Scheduled') {
    return { key: 'mark_picked', label: 'Mark Return Picked', color: STAGE_COLORS.green };
  }
  if (stage === 'Picked') {
    return { key: 'mark_received', label: 'Mark Return Received', color: STAGE_COLORS.green };
  }
  return null;
}